import { DEFAULT_LOCALE, localeSchema, type Locale } from '@warehouse/shared';
import { defaultNamespace, locales, namespaces, resources as sharedResources } from '@warehouse/shared/i18n';
import i18next, { type i18n as I18nInstance } from 'i18next';
import { initReactI18next } from 'react-i18next';

import { SHELL_NAMESPACE, shellResources } from './shell-catalog';

/**
 * i18next bootstrap.
 *
 * Catalogs are bundled, not fetched: they come from `@warehouse/shared/i18n`
 * (domain namespaces, shared with the API's mailer and Android) plus the
 * web-only `shell` namespace. A warehouse tablet must not need a second network
 * round trip to render its first screen, and a missing catalog request is one
 * more failure mode on a flaky floor AP.
 *
 * The language is NOT auto-detected from the browser. `users.language`
 * (ARCHITECTURE §5) is the source of truth: `AuthProvider` calls
 * `setLanguage(user.language)` after `/api/auth/me`, and the Settings page
 * persists changes through `PATCH /api/auth/profile`. `LANGUAGE_STORAGE_KEY`
 * only remembers the last choice so the LOGIN screen (where there is no user
 * yet) does not flash Swedish at an English speaker.
 */

export const LANGUAGE_STORAGE_KEY = 'wms.language';

export const SUPPORTED_LOCALES = locales;
export { SHELL_NAMESPACE } from './shell-catalog';

type LocalizedResources = Record<string, Record<string, unknown>>;

const resources: Record<Locale, LocalizedResources> = {
  sv: { ...sharedResources.sv, [SHELL_NAMESPACE]: shellResources.sv },
  en: { ...sharedResources.en, [SHELL_NAMESPACE]: shellResources.en },
  zh: { ...sharedResources.zh, [SHELL_NAMESPACE]: shellResources.zh },
};

export const allNamespaces = [...namespaces, SHELL_NAMESPACE] as const;

export function storedLanguage(): Locale {
  try {
    const raw = globalThis.localStorage?.getItem(LANGUAGE_STORAGE_KEY);
    const parsed = localeSchema.safeParse(raw);
    return parsed.success ? parsed.data : DEFAULT_LOCALE;
  } catch {
    // Private mode / disabled storage — Swedish is the operating language.
    return DEFAULT_LOCALE;
  }
}

let instance: I18nInstance | null = null;

export function initI18n(initialLanguage: Locale = storedLanguage()): I18nInstance {
  if (instance) return instance;

  void i18next.use(initReactI18next).init({
    resources,
    lng: initialLanguage,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...locales],
    ns: [...allNamespaces],
    defaultNS: defaultNamespace,
    // The catalogs are typed-parity enforced, so a miss is a bug, not a
    // fallback path — surfacing the key beats rendering an empty cell.
    returnNull: false,
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
  });

  instance = i18next;
  return i18next;
}

/** Change the UI language and remember it for the next cold start. */
export function setLanguage(locale: Locale): void {
  const i18n = initI18n();
  if (i18n.language !== locale) void i18n.changeLanguage(locale);
  try {
    globalThis.localStorage?.setItem(LANGUAGE_STORAGE_KEY, locale);
  } catch {
    /* storage disabled — the account language still drives the next login */
  }
  if (typeof document !== 'undefined') document.documentElement.lang = locale;
}

export function currentLocale(): Locale {
  const parsed = localeSchema.safeParse(instance?.language ?? DEFAULT_LOCALE);
  return parsed.success ? parsed.data : DEFAULT_LOCALE;
}

/** Test seam — i18next is a module singleton. */
export function __resetI18n(): void {
  instance = null;
}

export default i18next;
